import { Container, Graphics, Text } from 'pixi.js';

import { BaseWidget } from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';

/**
 * Vertical speed tape widget that displays a vertical tape with VS indication similar to the Garmin G5.
 * Shows major graduations every 500 FPM and minor divisions every 100 feet.
 */
export class VerticalSpeedTapeWidget extends BaseWidget {
    static readonly requiredProperties = ['altitudeFeet'] as const;
    static readonly optionalProperties = [] as const;

    private container: Container;
    private tapeGraphics: Graphics;
    private labelsContainer: Container;

    private currentVS = 0;
    private vsIndicator: Graphics;

    // Configuration constants
    private PIXELS_PER_FOOT = 10; // Scale factor for tape movement
    private readonly MAJOR_INTERVAL = 500; // Major graduations every 500 feet
    private readonly MINOR_INTERVAL = 100; // Minor divisions every 25 feet
    private readonly TAPE_BUFFER = 500; // Draw this many feet above/below viewport
    private readonly MAJOR_TICK_WIDTH = 7;
    private readonly MINOR_TICK_WIDTH = 3;

    async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        this.PIXELS_PER_FOOT = positionTranslator.y(1) / 3300;
        elementContainer.label = 'VS Tape Widget';
        // Create graphics object for the tape
        this.tapeGraphics = new Graphics();
        this.labelsContainer = new Container();

        // Create viewport mask to clip the tape to the visible area
        const viewportMask = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(0xffffff);
        elementContainer.addChild(viewportMask);
        elementContainer.mask = viewportMask;

        // Add tape and labels to container
        elementContainer.addChild(this.tapeGraphics);
        elementContainer.addChild(this.labelsContainer);

        // Create VS indicator
        this.vsIndicator = new Graphics();
        elementContainer.addChild(this.vsIndicator);

        this.drawTape(hudContext, positionTranslator);
    }

    protected updateWidget(
        point: CurrentPoint,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        const currentVS = point.verticalSpeed;

        this.vsIndicator.clear();

        const absVS = Math.abs(currentVS);
        if (absVS < 25) {
            // Do not draw indicator for very low vertical speeds
            return;
        }

        this.vsIndicator
            .rect(
                positionTranslator.x(1) - 6,
                currentVS < 0
                    ? positionTranslator.y(0.5)
                    : positionTranslator.y(0.5) - absVS * this.PIXELS_PER_FOOT,
                6,
                absVS * this.PIXELS_PER_FOOT
            )
            .fill('magenta');
    }

    /**
     * Draws the altitude tape with major and minor graduations.
     * Draws a range centered on current altitude to provide buffer for scrolling.
     */
    private drawTape(
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void {
        // Clear existing graphics
        this.tapeGraphics.clear();
        this.labelsContainer.removeChildren();

        const color = 'white';
        const centerY = positionTranslator.y(0.5);

        // Calculate altitude range to draw
        const minVS = -1500;
        const maxVS = 1500;

        // Draw vertical line for tape spine and all tick marks in one path
        // Spine on far right side, tick marks and labels extend to the left
        const spineX = positionTranslator.x(1);

        // Draw all graduations
        for (
            let renderVS = minVS;
            renderVS <= maxVS;
            renderVS += this.MINOR_INTERVAL
        ) {
            const isMajor = renderVS % this.MAJOR_INTERVAL === 0;
            const tickWidth = isMajor
                ? this.MAJOR_TICK_WIDTH
                : this.MINOR_TICK_WIDTH;

            // Calculate Y position relative to center
            const y = centerY - renderVS * this.PIXELS_PER_FOOT;

            // Add tick mark to path - extend to the RIGHT
            this.tapeGraphics.moveTo(spineX, y);
            this.tapeGraphics.lineTo(spineX - tickWidth, y);

            // Draw label for major graduations - position to the RIGHT of tick
            if (isMajor) {
                const label = new Text({
                    text: (renderVS / 100).toString(),
                    style: {
                        fontFamily: hudContext.globalConfig.fontFamily,
                        fontSize: hudContext.globalConfig.baseFontSize,
                        fill: color,
                    },
                });
                label.anchor.set(1, 0.5);
                label.position.set(spineX - tickWidth, y);
                this.labelsContainer.addChild(label);
            }
        }

        // Stroke all the lines at once
        this.tapeGraphics.stroke({ width: 1, color });
    }

    cleanUp(): void {
        this.tapeGraphics?.destroy();
        this.labelsContainer?.destroy();
        this.container?.destroy();

        this.tapeGraphics = null;
        this.labelsContainer = null;
        this.container = null;
    }
}
